import { Fragment, useEffect, useState } from "react";

const OFFICES = [
  "President",
  "Vice President",
  "Treasurer",
  "Secretary",
  "Membership Chair",
  "Sponsorship Chair",
  "Events Chair",
];

const today = () => new Date().toISOString().slice(0, 10);

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

const EMPTY = {
  office: "",
  holder_name: "",
  holder_email: "",
  started_on: today(),
  photo_url: "",
  bio: "",
  sort_order: "",
};

function AddForm({ chapterId, onAdded }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await api(`/api/chapters/${chapterId}/board`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (res.ok) {
      setForm({ ...EMPTY, started_on: today() });
      onAdded();
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error || "Could not add officer.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Office
          <input
            list="offices"
            name="office"
            required
            value={form.office}
            onChange={set("office")}
            placeholder="President"
          />
          <datalist id="offices">
            {OFFICES.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        </label>
        <label className="field">
          Name
          <input name="holder_name" required value={form.holder_name} onChange={set("holder_name")} placeholder="Full name" />
        </label>
        <label className="field">
          Email
          <input type="email" name="holder_email" value={form.holder_email} onChange={set("holder_email")} placeholder="optional" />
        </label>
        <label className="field">
          Term start
          <input type="date" name="started_on" required value={form.started_on} onChange={set("started_on")} />
        </label>
        <label className="field">
          Photo URL
          <input name="photo_url" value={form.photo_url} onChange={set("photo_url")} placeholder="optional" />
        </label>
        <label className="field">
          Sort order
          <input type="number" name="sort_order" value={form.sort_order} onChange={set("sort_order")} placeholder="0" />
        </label>
        <label className="field" style={{ gridColumn: "1 / -1" }}>
          Bio
          <textarea name="bio" value={form.bio} onChange={set("bio")} placeholder="Short bio (optional)" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit" disabled={busy}>
          {busy ? "Adding…" : "Add officer"}
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function TermEditForm({ term, onSaved, onCancel }) {
  const [form, setForm] = useState({
    office: term.office,
    holder_name: term.holder_name,
    holder_email: term.holder_email || "",
    photo_url: term.photo_url || "",
    bio: term.bio || "",
    sort_order: term.sort_order ?? 0,
    is_public: term.is_public,
  });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    const res = await api(`/api/board/${term.id}`, { method: "PATCH", body: JSON.stringify(form) });
    if (res.ok) onSaved();
    else setError((await res.json().catch(() => ({}))).error || "Could not save.");
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">Office<input name="office" value={form.office} onChange={set("office")} /></label>
        <label className="field">Name<input name="holder_name" value={form.holder_name} onChange={set("holder_name")} /></label>
        <label className="field">Email<input type="email" name="holder_email" value={form.holder_email} onChange={set("holder_email")} /></label>
        <label className="field">Photo URL<input name="photo_url" value={form.photo_url} onChange={set("photo_url")} /></label>
        <label className="field">Sort order<input type="number" name="sort_order" value={form.sort_order} onChange={set("sort_order")} /></label>
        <label className="field" style={{ alignSelf: "end" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" name="is_public" style={{ width: "auto" }} checked={!!form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
            Show publicly
          </span>
        </label>
        <label className="field" style={{ gridColumn: "1 / -1" }}>Bio<textarea name="bio" value={form.bio} onChange={set("bio")} /></label>
      </div>
      <div className="field-actions">
        <button className="btn-primary" style={{ width: "auto" }} type="submit">Save</button>
        <button className="btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        {error && <span className="err">{error}</span>}
      </div>
    </form>
  );
}

function TermRow({ term, canManage, onChange }) {
  const [editing, setEditing] = useState(false);

  async function patch(body) {
    await api(`/api/board/${term.id}`, { method: "PATCH", body: JSON.stringify(body) });
    onChange();
  }
  async function remove() {
    if (!window.confirm(`Remove ${term.office} (${term.holder_name})?`)) return;
    await api(`/api/board/${term.id}`, { method: "DELETE" });
    onChange();
  }

  return (
    <Fragment>
      <tr>
        <td><strong>{term.office}</strong></td>
        <td>
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {term.photo_url && (
              <img src={term.photo_url} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }} />
            )}
            <span>
              {term.holder_name}
              {!term.is_public && <span className="muted" style={{ fontSize: 12 }}> · hidden</span>}
              {term.holder_email && (
                <>
                  <br />
                  <span className="muted" style={{ fontSize: 12 }}>{term.holder_email}</span>
                </>
              )}
            </span>
          </span>
        </td>
        <td>
          {term.started_on}
          {term.ended_on ? ` – ${term.ended_on}` : " – present"}
        </td>
        {canManage && (
          <td className="row-actions">
            <button className="btn-ghost" onClick={() => setEditing(!editing)} type="button">
              {editing ? "Close" : "Edit"}
            </button>
            {term.is_current ? (
              <button className="btn-ghost" onClick={() => patch({ ended_on: today() })} type="button">End term</button>
            ) : (
              <button className="btn-ghost" onClick={() => patch({ ended_on: null })} type="button">Reinstate</button>
            )}
            <button className="btn-danger" onClick={remove} type="button">Remove</button>
          </td>
        )}
      </tr>
      {editing && (
        <tr>
          <td colSpan={canManage ? 4 : 3} style={{ background: "var(--surface-2)" }}>
            <TermEditForm term={term} onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function TermTable({ terms, canManage, onChange }) {
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>Office</th>
          <th>Holder</th>
          <th>Term</th>
          {canManage && <th />}
        </tr>
      </thead>
      <tbody>
        {terms.map((t) => (
          <TermRow key={t.id} term={t} canManage={canManage} onChange={onChange} />
        ))}
      </tbody>
    </table>
  );
}

export function BoardRoster({ chapterId, canManage }) {
  const [terms, setTerms] = useState(null);
  const [adding, setAdding] = useState(false);

  function load() {
    api(`/api/chapters/${chapterId}/board`)
      .then((res) => (res.ok ? res.json() : { terms: [] }))
      .then((data) => setTerms(data.terms));
  }

  useEffect(load, [chapterId]);

  if (terms === null) {
    return <p className="muted">Loading board…</p>;
  }

  const current = terms.filter((t) => t.is_current);
  const past = terms.filter((t) => !t.is_current);

  return (
    <>
      <div className="section">
        <div className="section-head">
          <h3>Current board</h3>
          {canManage && !adding && (
            <button className="btn-primary" style={{ width: "auto" }} onClick={() => setAdding(true)} type="button">
              Add officer
            </button>
          )}
        </div>
        {current.length ? (
          <TermTable terms={current} canManage={canManage} onChange={load} />
        ) : (
          <p className="empty">No current officers recorded.</p>
        )}
        {adding && (
          <AddForm
            chapterId={chapterId}
            onAdded={() => {
              setAdding(false);
              load();
            }}
          />
        )}
      </div>

      {past.length > 0 && (
        <div className="section">
          <div className="section-head">
            <h3>Past terms</h3>
          </div>
          <TermTable terms={past} canManage={canManage} onChange={load} />
        </div>
      )}
    </>
  );
}
